import React, { useCallback, useMemo } from 'react';
import type { NuvoPieChartProps } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import { NuvoPieChart } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import { Legend, Series, Label, Connector } from 'devextreme-react/pie-chart';
import type { PointClickEvent } from 'devextreme/viz/pie_chart';
import { DashboardStatsPoint } from '#/graphql/types';
import { toTitleCase } from '#/common/utils/formatters';
import { NOT_ASSIGNED_LABEL } from '../../utils/constants';
import { get } from 'lodash';

export interface PieChartProps {
  type?: NuvoPieChartProps['type'];
  center?: NuvoPieChartProps['center'];
  dataSource: DashboardStatsPoint[] | undefined | null;
  title?: string;
  subTitle?: string;
  startingDataColor?: NuvoPieChartProps['startingDataColor'];
  /** degrees between 0-360 */
  startingAngle?: number;
  legendMappings?: any;
  onItemClick?: (point: DashboardStatsPoint) => void;
}

const PieChart: React.FC<PieChartProps> = ({
  type = 'pie',
  center,
  dataSource,
  title,
  subTitle,
  startingDataColor = 'green',
  legendMappings,
  onItemClick,
}) => {
  const ds = useMemo(
    () =>
      dataSource
        ?.filter((i) => !!i)
        .map((i) => ({ ...i, value: i.value || 0, label: i.label || NOT_ASSIGNED_LABEL })) || [],
    [dataSource],
  );
  const labelFormat = useCallback((e: { argument: string; value: string; percentText: string }) => {
    return ` ${e.value} (${e.percentText})`;
  }, []);

  const legendFormat = useCallback(
    (e: { pointName: string; pointColor: string; pointIndex: number }) => {
      if (legendMappings) {
        return e.pointName === NOT_ASSIGNED_LABEL
          ? NOT_ASSIGNED_LABEL
          : get(legendMappings, `[${e.pointName}].label`, toTitleCase(e.pointName));
      }
      return toTitleCase(e.pointName);
    },
    [legendMappings],
  );

  const onPointClick = useCallback(
    (e: PointClickEvent) => {
      if (onItemClick) {
        onItemClick(e.target.data as DashboardStatsPoint);
      }
    },
    [onItemClick],
  );

  return (
    <NuvoPieChart
      showTooltip
      type={type}
      dataSource={ds}
      title={title}
      subtitle={subTitle}
      innerRadius={0.5}
      startingDataColor={startingDataColor}
      startAngle={90}
      onPointClick={onPointClick}
      center={center}>
      <Legend
        visible
        verticalAlignment="bottom"
        horizontalAlignment="center"
        customizeText={legendFormat}
      />

      <Series argumentField="label" valueField="value">
        <Label visible customizeText={labelFormat} backgroundColor="none" position="column">
          <Connector visible width={1} />
        </Label>
      </Series>
    </NuvoPieChart>
  );
};

export default React.memo(PieChart);
